import React, { type FC, useMemo, useEffect } from 'react'
import { type RouteComponentProps } from '@reach/router'
import { t } from 'i18next'
import { isNil } from 'lodash'
import { useActivities } from 'app-care/src/hooks/useActivities'
import { usePatientPathways } from 'app-care/src/hooks/usePatientPathways'
import { usePagination } from 'app-care/src/hooks/usePagination'
import type { Activity } from 'app-care/src/hooks/useActivityFeed'
import { possiblePatientActivityTypes } from '../../../components/WorkingListTable/utils'
import { ActivityStatus } from '../../Pathway/types'
import { type Patient } from '../types'
import { ActivitiesTable } from './ActivitiesTable'

interface ActvitiesTabProps {
  patient: Patient
}

export const ActivitiesTab: FC<RouteComponentProps<ActvitiesTabProps>> = ({ patient }) => {
  const { patientPathways } = usePatientPathways(patient?.id ?? '')

  // Pagination state for pending activities
  const pendingPagination = usePagination({
    initialPageSize: 10,
    initialSortField: 'date',
    initialSortDirection: 'desc'
  })

  // Pagination state for completed activities
  const completedPagination = usePagination({
    initialPageSize: 10,
    initialSortField: 'date',
    initialSortDirection: 'desc'
  })

  // Fetch pending activities with pagination
  const {
    loading: pendingLoading,
    activities: pendingActivities = [],
    pagination: pendingPaginationData
  } = useActivities(
    {
      patient_id: { eq: patient?.id ?? '' },
      activity_status: {
        in: [ActivityStatus.Active, ActivityStatus.Failed]
      }
    },
    pendingPagination.pagination,
    pendingPagination.sorting
  )

  // Fetch completed activities with pagination
  const {
    loading: completedLoading,
    activities: completedActivities = [],
    pagination: completedPaginationData
  } = useActivities(
    {
      patient_id: { eq: patient?.id ?? '' },
      activity_status: { in: [ActivityStatus.Done, ActivityStatus.Expired] },
      activity_type: { in: possiblePatientActivityTypes }
    },
    completedPagination.pagination,
    completedPagination.sorting
  )

  // Update total counts when data loads
  useEffect(() => {
    if (pendingPaginationData?.total_count) {
      pendingPagination.setTotalCount(pendingPaginationData.total_count)
    }
  }, [pendingPaginationData?.total_count])

  useEffect(() => {
    if (completedPaginationData?.total_count) {
      completedPagination.setTotalCount(completedPaginationData.total_count)
    }
  }, [completedPaginationData?.total_count])

  const getActivityPathwayTitle = useMemo(
    () =>
      (activity: Activity): string => {
        const pathway = patientPathways?.find(pathway => pathway.id === activity.context?.pathway_id)
        return pathway?.title ?? '-'
      },
    [patientPathways]
  )

  if (isNil(patient)) {
    return null
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <div className="font-bold text-lg">{t('pending_activities')}</div>
        <ActivitiesTable
          activities={pendingActivities}
          getActivityPathwayTitle={getActivityPathwayTitle}
          emptyMessage={t('workinglist:no_activities_found')}
          loading={pendingLoading}
          totalCount={pendingPaginationData?.total_count ?? 0}
          currentPage={pendingPagination.currentPage}
          perPage={pendingPagination.pagination.count}
          sorting={pendingPagination.sorting}
          onPageChange={pendingPagination.setPage}
          onSortChange={pendingPagination.setSorting}
        />
      </div>

      <div className="flex flex-col gap-4">
        <div className="font-bold text-lg">{t('completed_activities')}</div>
        <ActivitiesTable
          activities={completedActivities}
          getActivityPathwayTitle={getActivityPathwayTitle}
          emptyMessage={t('workinglist:no_activities_found')}
          loading={completedLoading}
          totalCount={completedPaginationData?.total_count ?? 0}
          currentPage={completedPagination.currentPage}
          perPage={completedPagination.pagination.count}
          sorting={completedPagination.sorting}
          onPageChange={completedPagination.setPage}
          onSortChange={completedPagination.setSorting}
        />
      </div>
      <div className="pb-5" />
    </div>
  )
}
